// src/components/HeaderSection.js
import React from 'react';
import { Helmet } from 'react-helmet';
import { Grid, Avatar, Box, Typography, IconButton } from '@mui/material';
import { FaFacebookF, FaInstagram } from 'react-icons/fa';
import ZillowIcon from './ZillowIcon'; // Custom component for Zillow logo
import me from './me.JPG';
import house1 from './house1.jpg';
import house2 from './house2.jpg';
import house3 from './house3.jpg';
import house4 from './house4.jpg';

const HeaderSection = () => (
  <header>

    <Helmet>
      <title>American Dream Realty | Ganesh Neupane, Broker/Owner</title>
      <meta
        name="description"
        content="Work with Ganesh Neupane, a top-performing Michigan realtor fluent in English, Nepali, Hindi, and Urdu. Serving Troy, Novi, Auburn Hills & Rochester Hills with personalized, data-driven real estate services."
      />
      <meta
        name="keywords"
        content="American Dream Realty, Southeast Michigan real estate, Troy, Novi, Auburn Hills, Rochester Hills, broker owner, real estate brokerage"
      />

      {/* Structured Data using JSON-LD for Google Search */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "RealEstateAgent",
          "name": "American Dream Realty",
          "description":
            "American Dream Realty is a premier real estate brokerage in Southeast Michigan, featuring top-performing agents with multilingual capabilities serving diverse families.",
          "url": "https://americandreamrealty.homes",
          "telephone": "+1-248-989-5737",
          "email": "americandreamrealty26@gmail.com",
          "areaServed": ["Troy, MI", "Novi, MI", "Auburn Hills, MI", "Rochester Hills, MI"],
          "knowsLanguage": ["en", "ne", "hi", "ur"],
          "contactPoint": {
            "@type": "ContactPoint",
            "contactType": "Customer Service",
            "telephone": "+1-248-989-5737",
            "email": "americandreamrealty26@gmail.com"
          },
          "sameAs": ["https://m.me/AmericanDreamRealty", "https://ganeshneupane.com"]
        })}
      </script>
    </Helmet>

    {/* Header Section with Layered Slideshow Background */}
    <Box
      sx={{
        position: 'relative',
        overflow: 'hidden',
        color: '#fff',
        padding: '60px 20px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '80vh',
        '&::after': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background: 'linear-gradient(to right, rgba(0,0,0,0.5), rgba(0,0,0,0.3))',
          zIndex: 1,
        },
      }}
    >
      {/* Slideshow Images with Blur Effect */}
      {[house1, house2, house3, house4].map((image, index) => (
        <Box
          key={index}
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundImage: `url(${image})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            opacity: 0,
            filter: 'blur(4px)', // Apply subtle blur effect
            animation: `fadeInOut 24s infinite ${index * 6}s`,
            zIndex: 0,
          }}
        />
      ))}

      {/* Keyframes for Smooth Fade Effect */}
      <style>
        {`
          @keyframes fadeInOut {
            0%, 10% { opacity: 0; }
            15%, 40% { opacity: 1; }
            45%, 100% { opacity: 0; }
          }
        `}
      </style>

      <Grid container spacing={4} alignItems="center" justifyContent="center" sx={{ position: 'relative', zIndex: 2 }}>
        {/* Left Side: Photo and Social Links */}
        <Grid item xs={12} md={4} data-aos="fade-right" sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Avatar
            alt="Ganesh Neupane"
            src={me}
            sx={{
              width: 250,          // Width for rectangular frame
              height: 300,         // Height for rectangular frame
              borderRadius: '8px', // Rounded corners for a rectangular shape
              border: '4px solid white',
              marginBottom: 2,
              marginTop: 8         // Adjust this value to move the avatar down
            }}
          />
          {/* Social Media Links */}
          <Box sx={{ display: 'flex', gap: 3 }}>
            <IconButton
              component="a"
              href="https://www.facebook.com/GaneshalpineRealtor/" // Replace with actual Facebook URL
              target="_blank"
              rel="noopener"
              aria-label="Facebook"
              sx={{ color: '#4267B2' }}
            >
              <FaFacebookF size={30} />
            </IconButton>
            <IconButton
              component="a"
              href="https://www.instagram.com/realtorganeshalpine?igsh=ZTN4MmJiOGVlcDd6" // Replace with actual Instagram URL
              target="_blank"
              rel="noopener"
              aria-label="Instagram"
              sx={{ color: '#E4405F' }}
            >
              <FaInstagram size={30} />
            </IconButton>
            <IconButton
              component="a"
              href="https://www.zillow.com/profile/realtorganesh" // Replace with actual Zillow URL
              target="_blank"
              rel="noopener"
              aria-label="Zillow"
              sx={{ color: '#0074E4' }}
            >
              <ZillowIcon width={30} height={30} />
            </IconButton>
          </Box>
        </Grid>

        {/* Right Side: Text Content */}
        <Grid item xs={12} md={8} data-aos="fade-left">
          {/* Quote and Author Section */}
          <Box sx={{ maxWidth: '600px', marginBottom: 3 }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 'bold',
                fontFamily: 'Roboto Slab, serif',
                fontStyle: 'italic',
                color: '#f5f5f5', // Light off-white color for contrast
                textShadow: '1px 1px 3px rgba(0, 0, 0, 0.7)', // Subtle shadow for depth
                textAlign: 'left', // Aligns all text to the same left margin
              }}
            >
              "A home is a place to start your story."
            </Typography>
            <Typography
              variant="subtitle2"
              sx={{
                fontFamily: 'Roboto Slab, serif',
                fontStyle: 'italic',
                color: '#e0e0e0',
                textShadow: '1px 1px 2px rgba(0, 0, 0, 0.5)',
                textAlign: 'right', // Aligns the author to the right
                display: 'block', // Forces the author to be on a new line
                marginTop: '4px',
              }}
            >
              - Anita Renfroe
            </Typography>
          </Box>

          {/* Introduction */}
          <Typography
            variant="subtitle1"
            sx={{
              mb: 3,
              color: '#e0e0e0', // Light gray for enhanced visibility
              fontSize: '1.1rem',
              textAlign: 'left', // Keeps consistent left alignment
              maxWidth: '600px',
            }}
          >
            Welcome to American Dream Realty. I'm Ganesh Neupane, Broker/Owner and your dedicated Realtor®, committed to maximizing your real estate potential across Southeast Michigan with data-driven strategies for buyers, sellers, and investors.
          </Typography>

          {/* Mission Statement */}
          <Typography
            variant="body1"
            sx={{
              lineHeight: 1.8,
              color: '#d9d9d9', // Light gray for readability
              fontSize: '1rem',
              textAlign: 'left', // Consistent alignment
              maxWidth: '600px',
            }}
          >
            We provide the human insight, negotiation skills, and project management that algorithms alone can't. From first consult to closing, we keep the process clear, strategic, and focused on long‑term value.
          </Typography>
        </Grid>
      </Grid>
    </Box>
  </header>
);

export default HeaderSection;
